import { useMemo } from 'react';

import {
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  USDC_ADDRESSES,
  USDT_ADDRESSES,
} from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { useTokenResults } from '@/hooks/tokenQueries/useTokenResults';
import { useWalletBalances } from '@/hooks/useWalletBalances';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { ChainAddress } from '@/utils/models';
import { isUsdStableAddress } from '@/utils/multichain';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import type { TokenBalance } from '@/utils/zerion/types';

export const NUMBER_OF_SUGGESTED_TOKENS = 3;
export const MIN_BALANCE_USD_FOR_SUGGESTION = 5;

const addressesMatch = (a: string | undefined, b: string | undefined, chainId: number): boolean =>
  !!a && !!b && normalizeTokenAddress(a, chainId) === normalizeTokenAddress(b, chainId);

/**
 * Ranks the wallet's held tokens (>= $5) on a chain into sell suggestions:
 * USDC/USDT first, then other USD stablecoins, then native, then anything else -
 * each tier ordered by USD balance. Excludes the given addresses (typically the
 * buy and current sell tokens) and caps at NUMBER_OF_SUGGESTED_TOKENS.
 *
 * Unlike the empty-side suggestions, this only surfaces tokens the wallet
 * actually holds, so the user can switch to something sellable in one tap.
 */
export const buildOwnedSuggestionAddresses = (
  chainId: number,
  balances: TokenBalance[],
  excludedAddresses: Set<string>,
): ChainAddress[] => {
  const usdc = USDC_ADDRESSES[chainId];
  const usdt = USDT_ADDRESSES[chainId];
  const native = NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId];

  const held = balances
    .filter(
      (balance) =>
        !!balance.balanceUsd &&
        balance.balanceUsd >= MIN_BALANCE_USD_FOR_SUGGESTION &&
        !excludedAddresses.has(normalizeTokenAddress(balance.tokenAddress, chainId)),
    )
    .sort((a, b) => (b.balanceUsd ?? 0) - (a.balanceUsd ?? 0));

  const isPrimaryStable = (address: string) =>
    addressesMatch(address, usdc, chainId) || addressesMatch(address, usdt, chainId);
  const isNative = (address: string) => addressesMatch(address, native, chainId);

  // Single pass over the (already balance-sorted) held tokens, bucketing each
  // into its tier. Tiers are mutually exclusive, so the first matching branch
  // wins and later branches don't need to re-check earlier predicates.
  const tier1: ChainAddress[] = [];
  const tier2: ChainAddress[] = [];
  const tier3: ChainAddress[] = [];
  const tier4: ChainAddress[] = [];

  for (const balance of held) {
    const address = balance.tokenAddress as ChainAddress;
    if (isPrimaryStable(address)) {
      tier1.push(address);
    } else if (isUsdStableAddress(address, chainId)) {
      tier2.push(address);
    } else if (isNative(address)) {
      tier3.push(address);
    } else {
      tier4.push(address);
    }
  }

  const ordered = [...tier1, ...tier2, ...tier3, ...tier4];

  return Array.from(new Set(ordered)).slice(0, NUMBER_OF_SUGGESTED_TOKENS);
};

/**
 * Builds the ranked, registry-resolved held-token sell suggestions for a
 * chain, excluding the buy and current sell tokens. Shared by the sell-field
 * chips and the MarketMode gate that decides whether to show them in place of
 * 50%/Max: returning resolved tokens (not addresses) lets the gate swap the
 * actions only when renderable chips actually exist.
 */
export const useOwnedSellSuggestions = ({
  chainId,
  accountAddress,
  buyTokenAddress,
  sellTokenAddress,
}: {
  chainId: number;
  accountAddress: ChainAddress | undefined;
  buyTokenAddress: ChainAddress | undefined;
  sellTokenAddress: ChainAddress | undefined;
}): TokenResult[] => {
  const { data: balances } = useWalletBalances({
    evmAddress: chainId === CHAIN_IDS.SOLANA ? undefined : accountAddress,
    solanaAddress: chainId === CHAIN_IDS.SOLANA ? accountAddress : undefined,
    chainIds: [chainId],
  });

  const addresses = useMemo(() => {
    const excluded = new Set(
      [buyTokenAddress, sellTokenAddress]
        .filter((address): address is ChainAddress => !!address)
        .map((address) => normalizeTokenAddress(address, chainId)),
    );
    return buildOwnedSuggestionAddresses(chainId, balances ?? [], excluded);
  }, [chainId, balances, buyTokenAddress, sellTokenAddress]);

  const { tokenMap } = useTokenResults(addresses, chainId);

  return useMemo(() => {
    const chainTokens = tokenMap?.[chainId];
    if (!chainTokens) return [];
    // Preserve the ranked order so the chips and analytics rank match the builder.
    return addresses
      .map((address) => chainTokens[normalizeTokenAddress(address, chainId)])
      .filter((token): token is TokenResult => !!token);
  }, [tokenMap, addresses, chainId]);
};
